import { Fragment } from "react";

import clsx from "clsx";
import { Check } from "lucide-react";

import Separator from "components/Separator";

interface StepperIndicatorProps {
  steps: any[];
  activeStep: number;
}

const StepperIndicator = ({ steps, activeStep }: StepperIndicatorProps) => {
  const currentStepTitle = steps[activeStep - 1]?.title || "";
  const nextStepTitle = steps[activeStep]?.title || "";
  const isLastStep = activeStep === steps.length;
  const progressPercentage = isLastStep ? 100 : (activeStep / steps.length) * 100;

  return (
    <>
      {/* Desktop version */}
      <div className="hidden md:flex justify-center items-start">
        {steps.map((step, index) => (
          <Fragment key={step.value}>
            <div className="flex flex-col items-center mx-4">
              <div
                className={clsx(
                  "w-6 h-6 flex justify-center items-center border rounded-full text-xs",
                  (index + 1 === activeStep || index + 1 < activeStep) && "bg-general-green-800 text-white",
                )}
              >
                {index + 1 >= activeStep ? index + 1 : <Check className="h-4 w-4" />}
              </div>
              <span
                className={clsx(
                  "text-xs mt-2 text-center font-semibold",
                  index + 1 <= activeStep ? "text-general-green-800 font-medium" : "text-general-gray-500",
                )}
              >
                {step.title}
              </span>
            </div>
            {index + 1 !== steps.length && (
              <Separator
                orientation="horizontal"
                className={clsx(
                  "flex-1 h-0.5 bg-general-gray-200 mt-3",
                  index + 1 <= activeStep && "bg-general-green-800",
                )}
              />
            )}
          </Fragment>
        ))}
      </div>

      {/* Mobile version */}
      <div className="md:hidden flex items-center justify-between">
        <div className="flex-1">
          <div className="font-semibold text-sm text-gray-900">
            {activeStep}. {currentStepTitle}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {isLastStep ? (
              "Završni korak"
            ) : (
              `Iduće: ${nextStepTitle}`
            )}
          </div>
        </div>
        
        <div className="relative w-12 h-12 ml-4">
          {/* Background circle */}
          <div className="w-12 h-12 rounded-full border-3 border-gray-300 flex items-center justify-center">
            <span className="text-xs font-semibold text-gray-600">
              {activeStep}/{steps.length}
            </span>
          </div>
          
          {/* Progress circle */}
          <svg 
            className="absolute top-0 left-0 w-12 h-12 transform -rotate-90"
            viewBox="0 0 48 48"
          >
            <circle
              cx="24"
              cy="24"
              r="22.5"
              stroke="currentColor"
              strokeWidth="3"
              fill="none"
              className="text-general-green-800"
              strokeDasharray={`${2 * Math.PI * 22.5}`}
              strokeDashoffset={`${2 * Math.PI * 22.5 * (1 - progressPercentage / 100)}`}
              style={{
                transition: 'stroke-dashoffset 0.3s ease-in-out',
              }}
            />
          </svg>
        </div>
      </div>
    </>
  );
};

export default StepperIndicator;